import { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { cn } from './ui/utils';
import { isGroqConfigured, queryLlama } from '../utils/groq';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface LandingPageChatbotProps {
  onGetStarted: () => void;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
}

type ChatLanguage = 'english' | 'filipino' | 'cebuano';

interface Service {
  id: string;
  name: string;
  requirements: string;
  estimatedTime: number;
  isActive: boolean;
  price?: number;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  isActive: boolean;
}

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

const DEFAULT_MESSAGE =
  "Hi! I'm the Serve Smart assistant. Ask me about registrar services, requirements, fees, or processing times. If you want to file a request, I'll point you to sign in.";

const QUICK_PROMPTS = [
  'What services are available?',
  'What are the requirements for TOR?',
  'How much is a diploma request?',
  'How long does CAV processing take?',
];

export function LandingPageChatbot({ onGetStarted }: LandingPageChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isAIEnabled, setIsAIEnabled] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: 'welcome', text: DEFAULT_MESSAGE, sender: 'bot' },
  ]);
  const [services, setServices] = useState<Service[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsAIEnabled(isGroqConfigured());
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const [servicesRes, faqsRes] = await Promise.all([
          fetch(`${API_BASE}/services`, {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          }),
          fetch(`${API_BASE}/faqs`, {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          }),
        ]);

        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices((servicesData.services || []).filter((service: Service) => service.isActive));
        }

        if (faqsRes.ok) {
          const faqsData = await faqsRes.json();
          setFaqs((faqsData.faqs || []).filter((faq: FAQ) => faq.isActive));
        }
      } catch (error) {
        console.error('Error loading landing page chatbot data:', error);
      }
    };

    void loadData();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const faqContext = useMemo(() => {
    const faqSummary = faqs.slice(0, 8).map((faq) => `Q: ${faq.question}\nA: ${faq.answer}`).join('\n\n');
    const serviceSummary = services
      .slice(0, 12)
      .map((service) => {
        const price = typeof service.price === 'number' && service.price > 0 ? `Fee: Php ${service.price.toFixed(2)}.` : 'Fee: ask registrar.';
        const eta = typeof service.estimatedTime === 'number' && service.estimatedTime > 0
          ? `Estimated time: ${formatTimeFromMinutes(service.estimatedTime)}.`
          : 'Estimated time: not listed.';
        const requirements = service.requirements?.trim() ? `Requirements: ${service.requirements}` : 'Requirements: ask registrar.';
        return `${service.name}. ${price} ${eta} ${requirements}`;
      })
      .join('\n');

    return [
      'You are a chatbot on the public Serve Smart landing page.',
      'Answer briefly and clearly for guests.',
      'Reply in the guest\'s latest language, and translate source information when needed instead of copying another language directly.',
      'Do not claim a guest can submit a request from chat.',
      'If the guest wants to request or file a document, tell them to click Get Started and sign in.',
      serviceSummary ? `Services:\n${serviceSummary}` : '',
      faqSummary ? `FAQs:\n${faqSummary}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
  }, [faqs, services]);

  const detectRelevantServices = (message: string) => {
    const lowerMessage = message.toLowerCase();
    return services.filter((service) => {
      const serviceName = service.name.toLowerCase();
      return lowerMessage.includes(serviceName)
        || (lowerMessage.includes('tor') && serviceName.includes('transcript'))
        || (lowerMessage.includes('transcript') && serviceName.includes('transcript'))
        || (lowerMessage.includes('cav') && serviceName.includes('cav'))
        || (lowerMessage.includes('diploma') && serviceName.includes('diploma'))
        || (lowerMessage.includes('certification') && serviceName.includes('certification'))
        || (lowerMessage.includes('clearance') && serviceName.includes('clearance'));
    });
  };

  const buildFallbackResponse = (message: string) => {
    const lowerMessage = message.toLowerCase();
    const matchedServices = detectRelevantServices(message);
    const language = detectChatLanguage(message);

    if (/(request|file|apply|submit|get started|sign in|signup|log in|login)/.test(lowerMessage)) {
      if (language === 'filipino') {
        return 'Para makapag-file ng request, i-click ang Get Started at mag-sign in sa iyong Serve Smart account. Maaari pa rin akong sumagot dito tungkol sa requirements, bayarin, at processing time.';
      }

      if (language === 'cebuano') {
        return 'Aron maka-file og request, i-click ang Get Started ug pag-sign in sa imong Serve Smart account. Maka-tabang gihapon ko dinhi sa requirements, bayad, ug processing time.';
      }

      return 'To file a request, click Get Started and sign in to your Serve Smart account. I can still answer questions about requirements, fees, and processing times here.';
    }

    if (/(available services|what services|services)/.test(lowerMessage)) {
      if (services.length === 0) {
        if (language === 'filipino') {
          return 'Maaari kitang tulungan tungkol sa enrollment, transcript, diploma processing, certification, clearance, at iba pang concern sa registrar. I-click ang Get Started para makita ang buong listahan ng serbisyo.';
        }

        if (language === 'cebuano') {
          return 'Maka-tabang ko nimo bahin sa enrollment, transcript, diploma processing, certification, clearance, ug uban pang concern sa registrar. I-click ang Get Started aron makita ang tibuok listahan sa serbisyo.';
        }

        return 'I can help with enrollment, transcripts, diploma processing, certifications, clearance, and other registrar concerns. Click Get Started to access the full service list.';
      }

      const serviceList = services
        .slice(0, 8)
        .map((service) => `• ${service.name}`)
        .join('\n');

      if (language === 'filipino') {
        return `Narito ang ilan sa mga serbisyong available sa Serve Smart:\n${serviceList}\n\nKung nais mong magsumite ng request, i-click ang Get Started.`;
      }

      if (language === 'cebuano') {
        return `Ania ang pipila ka serbisyo nga available sa Serve Smart:\n${serviceList}\n\nKung gusto ka mosumite og request, i-click ang Get Started.`;
      }

      return `Here are some registrar services available in Serve Smart:\n${serviceList}\n\nIf you want to submit a request, click Get Started.`;
    }

    if (/(how much|fee|price|cost|payment|bayad|magkano)/.test(lowerMessage) && matchedServices.length > 0) {
      return matchedServices
        .map((service) => {
          if (typeof service.price === 'number' && service.price > 0) {
            return `${service.name}: Php ${service.price.toFixed(2)}`;
          }
          return `${service.name}: fee not listed in the system right now`;
        })
        .join('\n');
    }

    if (/(requirement|requirements|needed|bring|prepare)/.test(lowerMessage) && matchedServices.length > 0) {
      return matchedServices
        .map((service) => `${service.name}: ${service.requirements?.trim() || 'Requirements are not listed right now.'}`)
        .join('\n\n');
    }

    if (/(how long|processing time|timeline|turnaround|days|estimated time)/.test(lowerMessage) && matchedServices.length > 0) {
      return matchedServices
        .map((service) => {
          if (typeof service.estimatedTime === 'number' && service.estimatedTime > 0) {
            return `${service.name}: about ${formatTimeFromMinutes(service.estimatedTime)}`;
          }
          return `${service.name}: processing time is not listed right now`;
        })
        .join('\n');
    }

    const matchingFaq = faqs.find((faq) => {
      const question = faq.question.toLowerCase();
      return lowerMessage.split(/\s+/).some((word) => word.length > 3 && question.includes(word));
    });

    if (matchingFaq) {
      if (language === 'filipino') {
        return `${matchingFaq.answer}\n\nKung kailangan mong magsumite ng request, i-click ang Get Started.`;
      }

      if (language === 'cebuano') {
        return `${matchingFaq.answer}\n\nKung kinahanglan ka mosumite og request, i-click ang Get Started.`;
      }

      return `${matchingFaq.answer}\n\nIf you need to submit a request, click Get Started.`;
    }

    if (language === 'filipino') {
      return 'Maaari kitang tulungan tungkol sa mga serbisyo ng registrar, requirements, bayarin, at processing time. Kung gusto mong magsumite ng request, i-click ang Get Started at mag-sign in.';
    }

    if (language === 'cebuano') {
      return 'Maka-tabang ko nimo bahin sa mga serbisyo sa registrar, requirements, bayad, ug processing time. Kung gusto ka mosumite og request, i-click ang Get Started ug pag-sign in.';
    }

    return 'I can help with registrar services, requirements, fees, and processing times. If you want to submit a request, click Get Started and sign in.';
  };

  const sendMessage = async (rawMessage: string) => {
    const trimmedMessage = rawMessage.trim();
    if (!trimmedMessage) return;

    setMessages((prev) => [...prev, { id: `${Date.now()}`, text: trimmedMessage, sender: 'user' }]);
    setInputValue('');
    setIsTyping(true);

    const fallbackText = buildFallbackResponse(trimmedMessage);

    if (!isAIEnabled) {
      setMessages((prev) => [...prev, { id: `${Date.now()}-bot`, text: fallbackText, sender: 'bot' }]);
      setIsTyping(false);
      return;
    }

    try {
      const response = await queryLlama(trimmedMessage, faqContext);
      setMessages((prev) => [...prev, { id: `${Date.now()}-bot`, text: response || fallbackText, sender: 'bot' }]);
    } catch {
      setMessages((prev) => [...prev, { id: `${Date.now()}-bot`, text: fallbackText, sender: 'bot' }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'fixed bottom-4 right-4 z-50 h-14 w-14 rounded-full shadow-lg sm:bottom-6 sm:right-6',
          'bg-blue-600 text-white transition-all duration-300 hover:scale-110 hover:bg-blue-700 active:bg-blue-800'
        )}
        size="icon"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6 animate-pulse" />}
      </Button>

      {isOpen && (
        <Card className="fixed bottom-20 right-4 z-50 flex h-[560px] w-[calc(100vw-2rem)] flex-col border-2 shadow-2xl sm:bottom-24 sm:right-6 sm:w-[26rem]">
          <div className="rounded-t-lg bg-primary p-4 text-primary-foreground">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">Serve Smart Assistant</h3>
                  {isAIEnabled && (
                    <div className="flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                      <Sparkles className="h-3 w-3" />
                      AI Powered
                    </div>
                  )}
                </div>
                <p className="text-xs opacity-90">Public help desk for guests and first-time visitors</p>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto bg-muted/20 p-4">
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((prompt) => (
                <Button
                  key={prompt}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-auto whitespace-normal text-left text-xs"
                  onClick={() => void sendMessage(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>

            {messages.map((message) => (
              <div
                key={message.id}
                className={cn('flex flex-col', message.sender === 'user' ? 'items-end' : 'items-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] whitespace-pre-line rounded-lg px-4 py-2 text-sm',
                    message.sender === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border bg-white'
                  )}
                >
                  {message.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="rounded-lg border border-border bg-white px-4 py-2 text-sm">
                  <div className="flex gap-1">
                    <div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                    <div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                    <div className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="space-y-3 rounded-b-lg border-t bg-white p-4">
            <Button type="button" onClick={onGetStarted} className="w-full">
              Get Started to Submit a Request
            </Button>

            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void sendMessage(inputValue);
                  }
                }}
                placeholder="Ask about fees, requirements, or processing times"
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button type="button" size="icon" onClick={() => void sendMessage(inputValue)} disabled={!inputValue.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

function formatTimeFromMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  if (minutes < 1440) {
    const hours = Math.round(minutes / 60);
    return hours === 1 ? '1h' : `${hours}h`;
  }

  const days = Math.round(minutes / 1440);
  return days === 1 ? '1d' : `${days}d`;
}

function detectChatLanguage(message: string): ChatLanguage {
  const sample = message.toLowerCase();

  if (/\b(unsa|unsay|unsaon|kanusa|kanus-a|ngano|asa|diin|pila|palihog|salamat|maayong|bisaya|cebuano|pwede ba|dili|wala|naa|karon|giunsa|kinahanglan|ug)\b/.test(sample)) {
    return 'cebuano';
  }

  if (/\b(ano|paano|kailan|saan|magkano|kailangan|maaari|pwede|salamat|opo|po|mga|naman|gusto|bayad)\b/.test(sample)) {
    return 'filipino';
  }

  return 'english';
}